// AddWorkersPopup.tsx
import React from "react";
import WorkerData from "../../Types/WorkerData";
import WorkerRepository from "../../Repositories/WorkerRepository";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import WorkerGetResponse from "../../Repositories/WorkerResponses/WorkerGetResponse";
import { Functions } from "../../Types/Enums/Functions";
import Search from "../Search/Search";
import WorkDay from "../../Types/WorkDay";
import WorkPeriod from "../../Types/WorkPeriod";
import dayjs, { Dayjs } from "dayjs";
import WorkDayRepository from "../../Repositories/WorkDayRepository";
import Enumerable from "linq";
import WorkDayService from "../../Services/WorkDayService";

import EditIcon from "@mui/icons-material/Edit"

interface AddWorkersPopupProps {
    workerList: WorkerData[];
    workDay: WorkDay | Dayjs;
    selectedPeriodId?: string | null;
    buttonLabel?: string;
    onClose: (newWorkDay: WorkDay) => void;
}

interface AddWorkersPopupState {
    show: boolean;
    selectedIds: Set<string>;
    workersList: WorkerData[];
    newFirst: string;
    newLast: string;
    query: string;
}

export default class AddWorkersPopup extends React.Component<AddWorkersPopupProps, AddWorkersPopupState> {
    static contextType = AuthContext;
    context!: AuthContextType;

    constructor(props: AddWorkersPopupProps) {
        super(props);
        this.state = {
            show: false,
            selectedIds: new Set<string>(),
            workersList: [],
            newFirst: "",
            newLast: "",
            query: ""
        };
    }

    public componentDidMount(): void {
        this.getAllWorkers();
        this.syncSelectionFromProps();
    }

    public componentDidUpdate(prevProps: AddWorkersPopupProps): void {
        const changed =
            !this.sameWorkDay(prevProps.workDay, this.props.workDay) ||
            prevProps.workerList !== this.props.workerList;
        if (changed) this.syncSelectionFromProps();
    }

    private sameWorkDay(a: WorkDay | Dayjs, b: WorkDay | Dayjs) {
        if (dayjs.isDayjs(a) && dayjs.isDayjs(b)) return a.isSame(b, "day");
        if (!dayjs.isDayjs(a) && !dayjs.isDayjs(b)) return a.Id === b.Id && a.Date === b.Date;
        return false;
    }

    private syncSelectionFromProps() {
        const base = this.getSelectedPeriodWorkers();
        this.setState({ selectedIds: new Set(base.map(w => w.Guid)) });
    }

    private getSelectedPeriod(workDay: WorkDay | Dayjs): WorkPeriod | null {
        if (dayjs.isDayjs(workDay)) return null;

        const periods = WorkDayService.getDisplayPeriods(workDay);
        if (periods.length === 0) return null;

        return periods.find((period) => period.Id === this.props.selectedPeriodId) ?? periods[0];
    }

    private getSelectedPeriodWorkers() {
        const selectedPeriod = this.getSelectedPeriod(this.props.workDay);
        if (selectedPeriod != null) return selectedPeriod.Workers ?? [];

        return dayjs.isDayjs(this.props.workDay)
            ? this.props.workerList
            : this.props.workDay.Workers ?? [];
    }

    private async getAllWorkers() {
        const result = await WorkerRepository.getAllWorkers(this.context.locationId);
        if (result.isFailed()) return;

        const loaded = Enumerable.from(result.result!.response.map((x: WorkerGetResponse) =>
            new WorkerData(x.id, x.userId, x.firstName, x.lastName, x.debt, 0, x.permissions, x.function, 0, 0)
        )).orderBy(x => x.FirstName).toArray() as WorkerData[];

        this.setState({ workersList: loaded });
    }

    private toggleSelect = (worker: WorkerData) => {
        const selected = new Set(this.state.selectedIds);
        selected.has(worker.Guid) ? selected.delete(worker.Guid) : selected.add(worker.Guid);
        this.setState({ selectedIds: selected });
    };

    private async saveAndClose() {
        const selected = this.state.workersList.filter(w => this.state.selectedIds.has(w.Guid));
        let workDay: WorkDay;

        if (dayjs.isDayjs(this.props.workDay)) {
            const dateStr = this.props.workDay.format("YYYY-MM-DD");
            const period = WorkDayService.createEmptyWorkPeriod(dateStr);
            period.Workers = selected;
            const create = WorkDayService.buildWorkDay(this.props.workDay, [period]);
            const result = await WorkDayRepository.addWorkDay(this.context.locationId, create);
            if (result.isFailed() || !result.result?.id) return;
            workDay = WorkDayService.buildWorkDay(this.props.workDay, [period]);
            workDay.Id = result.result.id;
        } else {
            const periods = WorkDayService.getDisplayPeriods(this.props.workDay);
            const selectedPeriod = this.getSelectedPeriod(this.props.workDay);
            const nextPeriods = selectedPeriod == null
                ? periods
                : periods.map((period) => period.Id === selectedPeriod.Id
                    ? new WorkPeriod(period.Id, period.Name, period.Date, period.TipPin, period.TipCash, selected)
                    : period
                );

            workDay = WorkDayService.buildWorkDay(this.props.workDay, nextPeriods);
            if (selectedPeriod != null) {
                await WorkDayRepository.updateWorkPeriod(
                    this.context.locationId,
                    this.props.workDay.Id,
                    new WorkPeriod(selectedPeriod.Id, selectedPeriod.Name, selectedPeriod.Date, selectedPeriod.TipPin, selectedPeriod.TipCash, selected)
                );
            }
        }

        this.setState({ show: false });
        this.props.onClose(workDay);
    }

    private close = () => this.setState({ show: false });
    private open = () => this.setState({ show: true });

    public render() {
        const accent = "#c79656";
        const textPrimary = "#0f172a";
        const textMuted = "rgba(0,0,0,0.62)";
        const borderSoft = "rgba(0,0,0,0.10)";
        const checkboxAccent = "#374151";
        const selectedRowBg = "rgba(209, 164, 105, 0.1)";

        const filtered = this.state.workersList.filter((w) => {
            const q = this.state.query.trim().toLowerCase();

            // Helper to remove accents (diacritics)
            const normalize = (str: string) =>
                str
                .normalize("NFD") // separate base characters from accents
                .replace(/[\u0300-\u036f]/g, "") // remove all combining marks
                .toLowerCase();

            if (!q) return true;

            const nq = normalize(q);

            return (
                normalize(w.FirstName).includes(nq) ||
                normalize(w.LastName).includes(nq) ||
                normalize(w.Username || "").includes(nq)
            );
        });

        return (
            <div>
                <button
                    onClick={this.open}
                    style={{
                        padding: "5px 12px",
                        borderRadius: 8,
                        border: `1px solid ${accent}55`,
                        background: `${accent}12`,
                        color: "#7c5a31",
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        letterSpacing: "0.2px",
                        transition: "background 120ms ease, border-color 120ms ease",
                    }}
                    onMouseEnter={e => {
                        (e.currentTarget as HTMLButtonElement).style.background = `${accent}22`;
                        (e.currentTarget as HTMLButtonElement).style.borderColor = `${accent}88`;
                    }}
                    onMouseLeave={e => {
                        (e.currentTarget as HTMLButtonElement).style.background = `${accent}12`;
                        (e.currentTarget as HTMLButtonElement).style.borderColor = `${accent}55`;
                    }}
                >
                    <EditIcon sx={{ fontSize: 13, color: "#7c5a31" }} />
                    {this.props.buttonLabel ?? "Edit"}
                </button>

                {this.state.show && (
                    <div
                        onClick={this.close}
                        style={{
                            position: "fixed",
                            width: "100%",
                            inset: 0,
                            background: "rgba(10,12,16,0.45)",
                            backdropFilter: "blur(6px)",
                            WebkitBackdropFilter: "blur(6px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 16,
                            zIndex: 1000
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: "72vw",
                                height: "56vh",
                                maxWidth: 980,
                                background: "linear-gradient(180deg, #ffffff 0%, #fbfbfb 100%)",
                                borderRadius: 16,
                                boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
                                border: "1px solid rgba(0,0,0,0.06)",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden"
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "14px 16px",
                                    background: "linear-gradient(180deg, rgba(199,150,86,0.10), rgba(199,150,86,0.04))",
                                    borderBottom: "1px solid " + borderSoft
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <div style={{ width: 8, height: 22, borderRadius: 8, background: accent }} />
                                    <div style={{ color: textPrimary, fontWeight: 800 }}>
                                        Add workers for {dayjs.isDayjs(this.props.workDay) ? this.props.workDay.format("D MMMM") : dayjs(this.props.workDay.Date).format("D MMMM")}
                                    </div>
                                </div>
                                <button
                                    onClick={this.close}
                                    aria-label="Close"
                                    style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: 10,
                                        border: "1px solid " + borderSoft,
                                        background: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer"
                                    }}
                                >
                                    ✕
                                </button>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: 10,
                                    padding: "10px 16px",
                                    borderBottom: "1px solid " + borderSoft,
                                    background: "#fff"
                                }}
                            >
                                <Search sx={{ width: 300 }} onChange={(v) => this.setState({ query: v })} />
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1.15fr 0.85fr",
                                    gap: 14,
                                    padding: 16,
                                    flex: 1,
                                    minHeight: 0
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
                                    <div style={{ fontSize: 12, color: textMuted, paddingLeft: 2 }}>Pick from team</div>
                                    <div
                                        style={{
                                            border: "1px solid " + borderSoft,
                                            borderRadius: 12,
                                            overflow: "hidden",
                                            background: "#fff",
                                            flex: 1,
                                            minHeight: 0,
                                            display: "flex",
                                            flexDirection: "column"
                                        }}
                                    >
                                        <div style={{ overflow: "auto", flex: 1 }}>
                                            {filtered.map((w) => {
                                                const checked = this.state.selectedIds.has(w.Guid);
                                                return (
                                                    <label
                                                        key={w.Guid}
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "auto 1fr auto",
                                                            alignItems: "center",
                                                            gap: 10,
                                                            padding: "12px 14px",
                                                            borderTop: "1px solid " + borderSoft,
                                                            cursor: "pointer",
                                                            background: checked ? selectedRowBg : "#fff",
                                                            transition: "background 140ms ease"
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => this.toggleSelect(w)}
                                                            style={{
                                                                width: 16,
                                                                height: 16,
                                                                cursor: "pointer",
                                                                accentColor: checkboxAccent
                                                            }}
                                                        />
                                                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                                                            <span style={{ fontWeight: 700, color: textPrimary }}>
                                                                {w.FirstName} {w.LastName}
                                                            </span>
                                                            <span style={{ fontSize: 12, color: textMuted }}>
                                                                {Functions[w.Function] || "—"}
                                                            </span>
                                                        </div>
                                                        {checked && (
                                                            <div
                                                                style={{
                                                                    padding: "4px 8px",
                                                                    borderRadius: 999,
                                                                    background: "rgba(199,150,86,0.18)",
                                                                    color: "#7c5a31",
                                                                    fontSize: 11,
                                                                    fontWeight: 800
                                                                }}
                                                            >
                                                                Selected
                                                            </div>
                                                        )}
                                                    </label>
                                                );
                                            })}
                                            {filtered.length === 0 && (
                                                <div
                                                    style={{
                                                        padding: 16,
                                                        textAlign: "center",
                                                        color: textMuted,
                                                        fontSize: 14
                                                    }}
                                                >
                                                    No matches.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
                                    <div style={{ fontSize: 12, color: textMuted, paddingLeft: 2 }}>Quick add</div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <input
                                            placeholder="First name"
                                            value={this.state.newFirst}
                                            onChange={(e) => this.setState({ newFirst: e.target.value })}
                                            style={{
                                                flex: 1,
                                                padding: "10px 12px",
                                                borderRadius: 10,
                                                border: "1px solid " + borderSoft,
                                                background: "#fff"
                                            }}
                                        />
                                        <input
                                            placeholder="Last name"
                                            value={this.state.newLast}
                                            onChange={(e) => this.setState({ newLast: e.target.value })}
                                            style={{
                                                flex: 1,
                                                padding: "10px 12px",
                                                borderRadius: 10,
                                                border: "1px solid " + borderSoft,
                                                background: "#fff"
                                            }}
                                        />
                                    </div>
                                    <button
                                        onClick={() => {}}
                                        style={{
                                            alignSelf: "flex-start",
                                            padding: "10px 14px",
                                            borderRadius: 999,
                                            border: `1px solid ${accent}`,
                                            background: "rgba(199,150,86,0.10)",
                                            color: "#7c5a31",
                                            fontWeight: 800,
                                            fontSize: 12,
                                            cursor: "pointer",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                        }}
                                    >
                                        + Add to selection
                                    </button>

                                    <div
                                        style={{
                                            marginTop: 6,
                                            padding: 12,
                                            borderRadius: 12,
                                            background: "linear-gradient(180deg, rgba(0,0,0,0.03), rgba(0,0,0,0.015))",
                                            border: "1px solid " + borderSoft,
                                            color: textMuted,
                                            fontSize: 12
                                        }}
                                    >
                                        Tip: add multiple names and confirm in one go.
                                    </div>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: 16,
                                    borderTop: "1px solid " + borderSoft,
                                    background: "#fff"
                                }}
                            >
                                <div
                                    style={{
                                        padding: "6px 10px",
                                        borderRadius: 999,
                                        background: "rgba(0,0,0,0.035)",
                                        color: textMuted,
                                        fontSize: 12,
                                        fontWeight: 700
                                    }}
                                >
                                    {this.state.selectedIds.size} selected
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <button
                                        onClick={this.close}
                                        style={{
                                            padding: "10px 14px",
                                            borderRadius: 10,
                                            border: "1px solid " + borderSoft,
                                            background: "#fff",
                                            fontWeight: 700,
                                            fontSize: 12,
                                            cursor: "pointer"
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => this.saveAndClose()}
                                        style={{
                                            padding: "10px 14px",
                                            borderRadius: 10,
                                            border: "none",
                                            background: accent,
                                            color: "#fff",
                                            fontWeight: 900,
                                            fontSize: 12,
                                            cursor: "pointer",
                                            boxShadow: "0 6px 18px rgba(199,150,86,0.4)"
                                        }}
                                    >
                                        Add selected
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
